import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import styles from './RoboticDataAdmin.module.css';
import {PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import arrowUpIcon from '../../static_files/ep_arrow-up-bold.svg';
import {orderAdminApi} from '../Admin/Orders/orderAdminApi';
import EpisodeDetailModal from './EpisodeDetailModal';
import EpisodeRejectionModal from './EpisodeRejectionModal';
import AdminBrowserDownloads, {ADMIN_DOWNLOAD_MAX_EPISODES} from './AdminBrowserDownloads';
import AdminOrderSelect from './AdminOrderSelect';
import AdminUploadEpisodeModal from './AdminUploadEpisodeModal';
import AdminUploadFilters, {EMPTY_UPLOAD_FILTER_SELECTIONS} from './AdminUploadFilters';

const PREVIEW_PALETTES = [
    ['#3b2e19', '#232c3d', '#332a1d', '#1e3027', '#4a3225', '#24303a', '#3e3518', '#332433'],
    ['#24243c', '#38272d', '#232f2f', '#412d1f', '#322437', '#27333b', '#4b351e', '#26324a'],
    ['#1f3038', '#3a2d1f', '#213728', '#34253d', '#29323f', '#283b27', '#43332a', '#233240'],
];
const COLLAPSED_EPISODE_COUNT = 4;
const EXPANDED_PAGE_SIZE = 20;
const MAX_EXPANDED_EPISODE_COUNT = 48;
const ADMIN_UPLOAD_GROUP_PAGE_SIZE = 24;
const CHECK_LABELS = {
    'camera_avg_fps_within_0.5_of_target': {code: 'E-001', description: 'Camera avg FPS outside allowed range'},
    'camera_max_gap_below_69ms': {code: 'E-002', description: 'Camera frame gap too large'},
    'camera_resolution_min_960x600': {code: 'E-003', description: 'Camera resolution below 960x600'},
    'camera_resolution_by_slot': {code: 'E-003', description: 'Camera resolution below required minimum (env/other: 960x600; left/right: 640x480)'},
    'camera_resolution_min_1280x720': {code: 'E-003', description: 'Camera resolution below 1280x720 (legacy check)'},
    'camera_resolution_min_640x480': {code: 'E-003', description: 'Camera resolution below 640x480 (legacy check)'},
    'camera_sync_below_34ms': {code: 'E-004', description: 'Camera streams not synchronized'},
    'joint_camera_sync_below_34ms': {code: 'E-005', description: 'Joint data not synced with camera frames'},
    'joint_avg_freq_above_45hz': {code: 'E-006', description: 'Joint avg frequency too low'},
};

function getEpisodeColor(uploadIndex, episodeIndex) {
    const palette = PREVIEW_PALETTES[uploadIndex % PREVIEW_PALETTES.length];
    return palette[episodeIndex % palette.length];
}

function formatBytes(bytes) {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    const digits = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(digits)} ${units[unitIndex]}`;
}

function formatShortDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    });
}

function getEpisodeRejectionRows(episode) {
    if (episode?.status === 'QA_REJECTED') return [{key: 'qa_rejected', description: episode.rejection?.reason || 'QA rejected', detail: episode.rejection?.rejected_at ? new Date(episode.rejection.rejected_at).toLocaleString() : ''}];
    const processingError = episode?.processing_error || {};
    const details = processingError?.details || {};
    const step = String(processingError?.step || '').trim().toLowerCase();
    const errorMessage = String(processingError?.error_message || '').trim();
    const validationCode = String(details?.validation_error_code || '').trim().toLowerCase();
    if (
        step.includes('duplicate')
        || validationCode.includes('duplicate')
        || errorMessage.toLowerCase().includes('duplicate')
    ) {
        return [{
            key: 'duplication',
            description: 'Duplication',
            detail: '',
        }];
    }

    const failedChecks = Array.isArray(details.failed_checks) ? details.failed_checks.filter(Boolean) : [];
    const failedCheckDetails = details.failed_check_details || {};
    const humanizeReason = (value) => {
        const text = String(value || '').trim();
        if (!text) return '';
        if (/invalidmagic/i.test(text)) return 'Invalid MCAP file format';
        if (/not a valid mcap file/i.test(text)) return 'Invalid MCAP file format';
        return text;
    };
    const formatScalar = (value) => {
        if (value === undefined || value === null || value === '') return '';
        if (Array.isArray(value)) return value.map(formatScalar).filter(Boolean).join(', ');
        if (typeof value === 'object') {
            const parts = Object.entries(value)
                .filter(([, nestedValue]) => nestedValue !== undefined && nestedValue !== null && nestedValue !== '')
                .map(([nestedKey, nestedValue]) => `${nestedKey}: ${formatScalar(nestedValue)}`)
                .filter(Boolean);
            return parts.join('; ');
        }
        return String(value);
    };
    const formatDetail = (value) => {
        if (value === undefined || value === null || value === '') return '';
        return formatScalar(value);
    };

    if (failedChecks.length) {
        return failedChecks.map((checkKey) => {
            const entry = CHECK_LABELS[checkKey];
            return {
                key: checkKey,
                description: entry?.description || checkKey,
                detail: formatDetail(failedCheckDetails[checkKey]),
            };
        });
    }

    const rawDetails = [];
    const hiddenDetailKeys = new Set([
        'failed_checks',
        'failed_check_details',
        'failed_episode_id',
        'failed_episode_key',
        'validated_at',
        'validation_timestamp',
        'passed_checks',
        'total_checks',
        'summary',
        'validation_error_code',
        'matched_episode_id',
        'matched_upload_id',
        'matched_status',
    ]);
    Object.entries(details || {}).forEach(([key, value]) => {
        if (hiddenDetailKeys.has(key) || value === undefined || value === null || value === '') return;
        if (Array.isArray(value) && !value.length) return;
        if (typeof value === 'object' && !Array.isArray(value)) {
            const entries = Object.entries(value)
                .filter(([, nestedValue]) => nestedValue !== undefined && nestedValue !== null && nestedValue !== '')
                .map(([nestedKey, nestedValue]) => `${nestedKey}: ${formatScalar(nestedValue)}`);
            if (entries.length) rawDetails.push(`${key}: ${entries.join('; ')}`);
            return;
        }
        if (key === 'error') {
            rawDetails.push(humanizeReason(Array.isArray(value) ? value.join(', ') : String(value)));
            return;
        }
        rawDetails.push(`${key}: ${formatScalar(value)}`);
    });
    if (rawDetails.length) {
        return rawDetails.map((description, index) => ({
            key: `detail-${index}`,
            description,
            detail: '',
        }));
    }

    let description = 'Episode failed processing';
    if (processingError.error && String(processingError.error).trim()) {
        description = humanizeReason(processingError.error);
    } else if (processingError.step === 'mcap_validation') {
        description = 'MCAP validation failed';
    } else if (processingError.error_message && processingError.error_message !== 'mcap validation failed') {
        description = humanizeReason(processingError.error_message);
    }

    return [{
        key: 'fallback',
        description,
        detail: '',
    }];
}

function isFailedUploadStatus(status) {
    return String(status || '').trim().toUpperCase() === 'DERIVED_VALIDATION_FAILED';
}

function isPartialUploadStatus(status) {
    return String(status || '').trim().toUpperCase() === 'DERIVED_PARTIALLY_READY';
}

function shouldShowUploadStatus(status) {
    const normalized = String(status || '').trim().toUpperCase();
    return normalized !== 'UPLOADED' && normalized !== 'UPLOADING' && normalized !== '';
}

function shouldShowAdminUploadGroup(status) {
    const normalized = String(status || '').trim().toUpperCase();
    return normalized !== 'UPLOADED' && normalized !== 'UPLOADING';
}

export function matchesBrowseEpisodeSearch(group, episode, searchValue) {
    const query = String(searchValue || '').trim().toLowerCase();
    if (!query) return true;
    const jobId = getBrowseJobId(query);
    if (jobId !== null) return Number(episode.job_id) === jobId;
    return `${group.scenario} ${group.environment} ${episode.upload_id} UPL-${episode.upload_id} ${episode.episode_id} JOB-${episode.job_id || ''}`
        .toLowerCase()
        .includes(query);
}

export function getBrowseJobId(searchValue) {
    const match = String(searchValue || '').trim().match(/^JOB-(\d+)$/i);
    if (!match) return null;
    const jobId = Number(match[1]);
    return Number.isSafeInteger(jobId) && jobId > 0 ? jobId : null;
}

export function sortEpisodesNewestFirst(episodes) {
    return [...(Array.isArray(episodes) ? episodes : [])]
        .sort((left, right) => Number(right.episode_id) - Number(left.episode_id));
}

function ShareIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="4" r="1.5"/>
            <circle cx="4" cy="8" r="1.5"/>
            <circle cx="12" cy="12" r="1.5"/>
            <path d="M5.5 8.8l5 2.5M10.5 4.8l-5 2.5"/>
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="4.5"/>
            <path d="M10.5 10.5l3 3"/>
        </svg>
    );
}

function UploadTabIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M8 2v8m-3-3 3 3 3-3"/>
            <path d="M2 13h12"/>
        </svg>
    );
}

function ScenariosIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" rx="1"/>
            <rect x="9" y="1" width="6" height="6" rx="1"/>
            <rect x="1" y="9" width="6" height="6" rx="1"/>
            <rect x="9" y="9" width="6" height="6" rx="1"/>
        </svg>
    );
}

export function OrderEpisodeBrowser({
    adminToken,
    orders,
    selectedOrderId,
    onSelectOrder,
    scenarioGroups,
    selectedEpisodeIds,
    selectedEpisodes,
    assignedEpisodes,
    assignedEpisodeIds,
    onToggleEpisode,
    onToggleEpisodes,
    onToggleTaskEpisodes,
    onClearSelection,
    previewVideoUrls,
    previewImageUrls,
    previewLoadingIds,
    searchValue,
    onSearch,
    loading,
    error,
    expandedTaskIds,
    expandedTaskVisibleCounts,
    onToggleExpand,
    onLoadMoreEpisodes,
    onRequestAssign,
    selectingTaskIds,
    onRejectEpisode,
}) {
    const [activeEpisodeDetail, setActiveEpisodeDetail] = useState(null);
    const [taskDetailsById, setTaskDetailsById] = useState({});
    const [failedTaskDetailEpisodeId, setFailedTaskDetailEpisodeId] = useState(null);
    const activeDetailEpisodeId = activeEpisodeDetail?.rawEpisode.episode_id;
    const activeTaskKey = activeEpisodeDetail ? String(activeEpisodeDetail.group.taskId ?? '') : '';
    const activeTaskDetail = taskDetailsById[activeTaskKey] || null;
    const isLoadingTaskDetail = Boolean(activeEpisodeDetail) && !activeTaskDetail
        && failedTaskDetailEpisodeId !== activeDetailEpisodeId;
    const selectedSet = new Set(selectedEpisodeIds.map(String));
    const assignedSet = new Set(assignedEpisodeIds.map(String));
    const selectedHours = selectedEpisodes.reduce(
        (total, episode) => total + Number(episode.video_duration_hours || 0),
        0,
    );
    useEffect(() => {
        if (!activeDetailEpisodeId || activeTaskDetail) return undefined;
        // Same admin endpoint as AdminUploadEpisodeModal; it returns the full task regardless of environment visibility.
        const controller = new AbortController();
        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/episodes/${activeDetailEpisodeId}/detail`, {
            headers: {Authorization: `Bearer ${adminToken}`},
            signal: controller.signal,
        })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json?.success || !json.data?.task) {
                    throw new Error(getApiErrorMessage(json, 'Failed to load task details.'));
                }
                setTaskDetailsById((current) => ({...current, [activeTaskKey]: json.data.task}));
            })
            .catch((detailError) => {
                if (controller.signal.aborted) return;
                console.error('Failed to load task details for episode', detailError);
                setFailedTaskDetailEpisodeId(activeDetailEpisodeId);
            });
        return () => controller.abort();
    }, [activeDetailEpisodeId, activeTaskDetail, activeTaskKey, adminToken]);

    const assignedById = new Map(
        assignedEpisodes.map((episode) => [String(episode.episode_id), episode]),
    );
    const selectedGroups = Array.from(selectedEpisodes.reduce((groups, episode) => {
        const taskId = Number(episode.task_id);
        const key = Number.isInteger(taskId) ? String(taskId) : String(episode.task_name || 'uncategorized');
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                taskName: episode.task_name || 'Untitled task',
                episodes: [],
            });
        }
        groups.get(key).episodes.push(episode);
        return groups;
    }, new Map()).values());

    const openEpisodeDetail = (group, episode) => {
        const groupEpisodes = Array.isArray(group.episodes) ? group.episodes : [];
        const qualityScores = groupEpisodes
            .map((item) => Number(item.quality_score))
            .filter(Number.isFinite);
        const durations = groupEpisodes
            .map((item) => Number(item.video_duration_hours))
            .filter(Number.isFinite);
        setActiveEpisodeDetail({
            group,
            rawEpisode: episode,
            episode: {
                id: episode.episode_id,
                episodeId: episode.episode_id,
                taskTitle: group.scenario,
                qualityScore: episode.quality_score,
                durationHours: episode.video_duration_hours,
                machineProductName: episode.product_name,
                mp4Count: episode.video_count,
                mcapCount: 1,
                previewVideoUrl: previewVideoUrls[String(episode.episode_id)] || '',
                previewImageUrl: previewImageUrls[String(episode.episode_id)] || '',
            },
            taskAverageQuality: qualityScores.length
                ? qualityScores.reduce((total, value) => total + value, 0) / qualityScores.length
                : null,
            averageDurationHours: durations.length
                ? durations.reduce((total, value) => total + value, 0) / durations.length
                : null,
            uploadEpisodeCount: groupEpisodes.filter(
                (item) => String(item.upload_id) === String(episode.upload_id),
            ).length,
        });
    };

    return (
        <div className={styles.orderBrowserShell}>
            <div className={styles.orderBrowserMain}>
                <div className={styles.orderBrowserToolbar}>
                    <div className={styles.orderPickerField}>
                        <label htmlFor="admin-order-episode-order">Order</label>
                        <AdminOrderSelect
                            id="admin-order-episode-order"
                            orders={orders}
                            value={selectedOrderId}
                            onChange={onSelectOrder}
                        />
                    </div>
                    <div className={styles.orderBrowserSearch}>
                        <SearchIcon/>
                        <input
                            value={searchValue}
                            onChange={(event) => onSearch(event.target.value)}
                            placeholder="Search scenario, JOB-55, upload, or episode ID…"
                            disabled={!selectedOrderId}
                        />
                    </div>
                </div>

                {!selectedOrderId ? (
                    <div className={styles.orderBrowserEmpty}>
                        Select a Production or Delivering Order to browse matching Episodes.
                    </div>
                ) : loading ? (
                    <div className={styles.orderBrowserEmpty}>Loading matching Episodes…</div>
                ) : error ? (
                    <div className={styles.orderBrowserEmpty}>{error}</div>
                ) : !scenarioGroups.length ? (
                    <div className={styles.orderBrowserEmpty}>No delivery-ready Episodes match this Order.</div>
                ) : (
                    <div className={styles.scenarioBrowserList}>
                        {scenarioGroups.map((group) => {
                            const isExpanded = Boolean(expandedTaskIds[group.taskId]);
                            const visibleCount = isExpanded
                                ? Math.min(
                                    Number(expandedTaskVisibleCounts[group.taskId] || EXPANDED_PAGE_SIZE),
                                    MAX_EXPANDED_EPISODE_COUNT,
                                )
                                : COLLAPSED_EPISODE_COUNT;
                            const visibleEpisodes = group.episodes.slice(0, visibleCount);
                            const availableEpisodeCount = Math.min(
                                group.episodes.length,
                                MAX_EXPANDED_EPISODE_COUNT,
                            );
                            const hasMoreEpisodes = isExpanded && visibleCount < availableEpisodeCount;
                            const totalEpisodeCount = Number(group.episodeCount || group.episodes.length);
                            const taskAssignedCount = assignedEpisodes.filter(
                                (episode) => Number(episode.task_id) === Number(group.taskId)
                                    && matchesBrowseEpisodeSearch(group, episode, searchValue),
                            ).length;
                            const taskSelectedCount = selectedEpisodes.filter(
                                (episode) => Number(episode.task_id) === Number(group.taskId)
                                    && matchesBrowseEpisodeSearch(group, episode, searchValue),
                            ).length;
                            const selectableEpisodeCount = Math.max(0, totalEpisodeCount - taskAssignedCount);
                            const allSelectableSelected = selectableEpisodeCount > 0
                                && taskSelectedCount >= selectableEpisodeCount;
                            const someSelectableSelected = taskSelectedCount > 0;
                            const isSelectingTask = Boolean(selectingTaskIds[group.taskId]);
                            return (
                                <section key={group.key} className={styles.scenarioBrowserSection}>
                                    <header className={styles.scenarioBrowserHeader}>
                                        <button
                                            type="button"
                                            className={`${styles.browseCheckbox} ${styles.taskSelectCheckbox} ${allSelectableSelected ? styles.browseCheckboxChecked : ''} ${someSelectableSelected && !allSelectableSelected ? styles.browseCheckboxMixed : ''}`}
                                            disabled={!selectableEpisodeCount || isSelectingTask}
                                            onClick={() => onToggleTaskEpisodes(group, !allSelectableSelected)}
                                            role="checkbox"
                                            aria-checked={someSelectableSelected && !allSelectableSelected ? 'mixed' : allSelectableSelected}
                                            aria-label={isSelectingTask
                                                ? `Selecting all episodes for ${group.scenario}`
                                                : `${allSelectableSelected ? 'Deselect' : 'Select'} all episodes for ${group.scenario}`}
                                            title={`${allSelectableSelected ? 'Deselect' : 'Select'} all ${selectableEpisodeCount} available episodes`}
                                        />
                                        <div className={styles.scenarioBrowserTitleWrap}>
                                            <h3>{group.scenario}</h3>
                                            <button
                                                type="button"
                                                className={styles.scenarioChevron}
                                                onClick={() => onToggleExpand(group.taskId)}
                                                aria-label={isExpanded ? 'Collapse task' : 'Expand task'}
                                            >
                                                <img
                                                    src={arrowUpIcon}
                                                    alt=""
                                                    className={isExpanded ? styles.scenarioChevronOpen : ''}
                                                />
                                            </button>
                                            <span className={styles.scenarioEpisodeCount}>
                                                {totalEpisodeCount} episodes · {taskAssignedCount} in order · {taskSelectedCount} selected
                                            </span>
                                        </div>
                                    </header>
                                    <div className={styles.scenarioEpisodeGrid}>
                                        {visibleEpisodes.map((episode) => {
                                            const episodeKey = String(episode.episode_id);
                                            const selected = selectedSet.has(episodeKey);
                                            const assigned = assignedSet.has(episodeKey);
                                            const previewVideoUrl = String(previewVideoUrls[episodeKey] || '').trim();
                                            const previewImageUrl = String(previewImageUrls[episodeKey] || '').trim();
                                            const previewLoading = Boolean(previewLoadingIds[episodeKey]);
                                            return (
                                                <article
                                                    key={episodeKey}
                                                    className={`${styles.orderEpisodeCard} ${selected ? styles.orderEpisodeCardSelected : ''} ${assigned ? styles.orderEpisodeCardAssigned : ''}`}
                                                    onClick={() => openEpisodeDetail(group, episode)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            openEpisodeDetail(group, episode);
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Open episode ${episode.episode_id} details`}
                                                >
                                                    <div className={styles.orderEpisodePreview}>
                                                        {previewVideoUrl ? (
                                                            <video src={previewVideoUrl} muted loop autoPlay playsInline preload="metadata"/>
                                                        ) : previewImageUrl ? (
                                                            <img src={previewImageUrl} alt="" loading="lazy"/>
                                                        ) : (
                                                            <span>{previewLoading ? 'Loading preview…' : 'Preview unavailable'}</span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className={`${styles.browseCheckbox} ${styles.orderEpisodeCheck} ${selected || assigned ? styles.browseCheckboxChecked : ''}`}
                                                            disabled={assigned}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                onToggleEpisode({
                                                                    ...episode,
                                                                    task_id: group.taskId,
                                                                    task_name: group.scenario,
                                                                });
                                                            }}
                                                            role="checkbox"
                                                            aria-checked={selected || assigned}
                                                            aria-label={assigned
                                                                ? `Episode ${episode.episode_id} is already in order`
                                                                : `${selected ? 'Deselect' : 'Select'} episode ${episode.episode_id}`}
                                                        />
                                                        {assigned ? <span className={styles.orderEpisodeAssignedBadge}>In Order</span> : null}
                                                        {onRejectEpisode && <button type="button" onClick={(event) => { event.stopPropagation(); setActiveEpisodeDetail(null); onRejectEpisode(episode); }}>Reject episode</button>}
                                                    </div>
                                                    <div className={styles.orderEpisodeMeta}>
                                                        <strong>Episode: {episode.episode_id}</strong>
                                                        <span>UPL-{episode.upload_id} · {(Number(episode.video_duration_hours || 0) * 3600).toFixed(1)}s</span>
                                                        <span>Quality Score: {episode.quality_score ?? '—'} · {episode.product_name || 'Unknown robot'}</span>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                    {hasMoreEpisodes ? (
                                        <div className={styles.groupLoadMoreWrap}>
                                            <button
                                                type="button"
                                                className={styles.loadMoreButton}
                                                onClick={() => onLoadMoreEpisodes(group.taskId)}
                                            >
                                                Load more
                                            </button>
                                        </div>
                                    ) : null}
                                </section>
                            );
                        })}
                    </div>
                )}
            </div>

            <aside className={styles.orderSelectionPanel}>
                <div className={styles.orderSelectionHeader}>
                    <div>
                        <span>Selected episodes</span>
                        <strong>{selectedEpisodeIds.length}</strong>
                    </div>
                    <span>{selectedHours.toFixed(2)} hours</span>
                    {selectedEpisodeIds.length ? (
                        <button type="button" className={styles.clearEpisodeSelection} onClick={onClearSelection} aria-label="Clear selected episodes">×</button>
                    ) : null}
                </div>
                <div className={styles.orderSelectionActions}>
                    <button
                        type="button"
                        className={styles.assignOrderButton}
                        disabled={!selectedOrderId || !selectedEpisodeIds.length}
                        onClick={onRequestAssign}
                    >
                        {selectedOrderId ? `Add to ${selectedOrderId}` : 'Select an Order'}
                    </button>
                    <p className={styles.orderSelectionHint}>
                        This adds delivery access only. Original Jobs, Uploads, and operator payments do not change.
                    </p>
                </div>
                <div className={styles.orderSelectionList}>
                    {selectedGroups.length ? selectedGroups.map((group) => {
                        const groupHours = group.episodes.reduce(
                            (total, episode) => total + Number(episode.video_duration_hours || 0),
                            0,
                        );
                        const visibleEpisodes = group.episodes.slice(0, 7);
                        const hiddenEpisodes = group.episodes.slice(7);
                        return (
                            <section className={styles.selectedTaskGroup} key={group.key}>
                                <div className={styles.selectedTaskSummary}>
                                    <span title={group.taskName}>{group.taskName}</span>
                                    <strong>{group.episodes.length} episodes · {groupHours.toFixed(2)} hours</strong>
                                </div>
                                <div className={styles.selectedEpisodeChips}>
                                    {visibleEpisodes.map((episode) => (
                                        <span className={styles.selectedEpisodeChip} key={episode.episode_id}>
                                            {episode.episode_id}
                                            <button type="button" onClick={() => onToggleEpisode(episode)} aria-label={`Remove episode ${episode.episode_id}`}>×</button>
                                        </span>
                                    ))}
                                    {hiddenEpisodes.length ? (
                                        <span className={`${styles.selectedEpisodeChip} ${styles.selectedEpisodeChipSummary}`}>
                                            … {hiddenEpisodes.length} episodes
                                            <button type="button" onClick={() => onToggleEpisodes(hiddenEpisodes, false)} aria-label={`Remove ${hiddenEpisodes.length} hidden episodes`}>×</button>
                                        </span>
                                    ) : null}
                                </div>
                            </section>
                        );
                    }) : <p>Select delivery-ready Episodes from the catalog.</p>}
                </div>
            </aside>
            {activeEpisodeDetail ? (
                <EpisodeDetailModal
                    adminToken={adminToken}
                    adminMode
                    hideDownload
                    episode={activeEpisodeDetail.episode}
                    task={{
                        scenario: activeEpisodeDetail.group.scenario,
                        ...activeTaskDetail,
                        ...(isLoadingTaskDetail ? {details_description: 'Loading…'} : {}),
                    }}
                    scenarioEpisodeCount={activeEpisodeDetail.group.episodeCount}
                    uploadEpisodeCount={activeEpisodeDetail.uploadEpisodeCount}
                    taskAverageQuality={activeEpisodeDetail.taskAverageQuality}
                    averageDurationHours={activeEpisodeDetail.averageDurationHours}
                    isSelected={selectedSet.has(String(activeEpisodeDetail.rawEpisode.episode_id))}
                    inOrder={assignedById.has(String(activeEpisodeDetail.rawEpisode.episode_id))}
                    onClose={() => setActiveEpisodeDetail(null)}
                    onToggleSelect={() => onToggleEpisode({
                        ...activeEpisodeDetail.rawEpisode,
                        task_id: activeEpisodeDetail.group.taskId,
                        task_name: activeEpisodeDetail.group.scenario,
                    })}
                />
            ) : null}
        </div>
    );
}

const RoboticDataAdmin = ({adminToken: adminTokenProp = ''}) => {
    const {showNotification, handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const adminToken = adminTokenProp || localStorage.getItem('prismax_admin_token') || '';
    const hasAdminAccess = Boolean(adminToken);
    const showNotificationRef = useRef(showNotification);
    const handleAdminSessionExpiredRef = useRef(handleAdminSessionExpired);
    const uploadGroupsRequestIdRef = useRef(0);
    const uploadGroupsAbortControllerRef = useRef(null);

    const [adminView, setAdminView] = useState('uploads');
    const [downloadEpisodeDetails, setDownloadEpisodeDetails] = useState({});
    const downloadEpisodeIds = Object.keys(downloadEpisodeDetails).map(Number);
    const [isAdminDownloading, setIsAdminDownloading] = useState(false);

    useEffect(() => {
        setDownloadEpisodeDetails({});
        setIsAdminDownloading(false);
    }, [adminToken]);

    const removeDownloadEpisode = (id) => setDownloadEpisodeDetails((current) => {
        const next = {...current}; delete next[id]; return next;
    });
    const toggleDownloadEpisode = (episode) => {
        if (episode.status === 'QA_REJECTED') return;
        const id = Number(episode.episode_id);
        setDownloadEpisodeDetails((current) => {
            if (current[id]) { const next = {...current}; delete next[id]; return next; }
            return Object.keys(current).length < ADMIN_DOWNLOAD_MAX_EPISODES ? {...current, [id]: episode} : current;
        });
    };

    const [eligibleOrders, setEligibleOrders] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState('');
    const [assignedOrderEpisodeIds, setAssignedOrderEpisodeIds] = useState([]);
    const [assignedOrderEpisodes, setAssignedOrderEpisodes] = useState([]);
    const [orderEpisodesRevision, setOrderEpisodesRevision] = useState(0);
    const [selectedOrderEpisodeIds, setSelectedOrderEpisodeIds] = useState([]);
    const [selectedOrderEpisodeDetails, setSelectedOrderEpisodeDetails] = useState({});
    const [selectingBrowseTaskIds, setSelectingBrowseTaskIds] = useState({});
    const [browseSearchValue, setBrowseSearchValue] = useState('');
    const [debouncedBrowseSearchValue, setDebouncedBrowseSearchValue] = useState('');
    const [expandedBrowseTasks, setExpandedBrowseTasks] = useState({});
    const [expandedBrowseTaskVisibleCounts, setExpandedBrowseTaskVisibleCounts] = useState({});
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [assignmentConfirmationOpen, setAssignmentConfirmationOpen] = useState(false);
    const [isAssigningEpisodes, setIsAssigningEpisodes] = useState(false);
    const [uploadGroups, setUploadGroups] = useState([]);
    useEffect(() => {
        const rejectedIds = uploadGroups.flatMap((group) => (group.episodes || [])
            .filter((episode) => episode.status === 'QA_REJECTED').map((episode) => episode.episode_id));
        if (rejectedIds.length) setDownloadEpisodeDetails((current) => {
            const next = {...current};
            rejectedIds.forEach((id) => delete next[id]);
            return next;
        });
    }, [uploadGroups]);
    const [uploadGroupsOffset, setUploadGroupsOffset] = useState(0);
    const [hasMoreUploadGroups, setHasMoreUploadGroups] = useState(false);
    const [episodePreviewUrls, setEpisodePreviewUrls] = useState({});
    const [episodePreviewImageUrls, setEpisodePreviewImageUrls] = useState({});
    const [episodePreviewLoadingIds, setEpisodePreviewLoadingIds] = useState({});
    const [episodePreviewVideoStates, setEpisodePreviewVideoStates] = useState({});
    const [episodePreviewResolvedIds, setEpisodePreviewResolvedIds] = useState({});
    const [pendingLoadMorePreviewIds, setPendingLoadMorePreviewIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMoreUploadGroups, setIsLoadingMoreUploadGroups] = useState(false);
    const [error, setError] = useState('');
    const [expandedUploads, setExpandedUploads] = useState({});
    const [expandedUploadVisibleCounts, setExpandedUploadVisibleCounts] = useState({});
    const [failedOnly, setFailedOnly] = useState(false);
    const [publicOnly, setPublicOnly] = useState(false);
    const [includeTestUploads, setIncludeTestUploads] = useState(false);
    const [uploadFilterSelections, setUploadFilterSelections] = useState(EMPTY_UPLOAD_FILTER_SELECTIONS);
    const [uploadDateRange, setUploadDateRange] = useState(null);
    const [uploadFilterOptions, setUploadFilterOptions] = useState(null);
    const [qaRejectedOnly, setQaRejectedOnly] = useState(false);
    const [canReject, setCanReject] = useState(false);
    const [rejectingEpisode, setRejectingEpisode] = useState(null);
    const [rejectingUpload, setRejectingUpload] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setCanReject(false);
        setRejectingEpisode(null);
        if (adminToken) fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/episode-rejections/access`, {
            headers: {Authorization: `Bearer ${adminToken}`},
        }).then(async (response) => {
            const json = await response.json();
            if (!cancelled) setCanReject(response.ok && json.success && json.can_reject === true);
        }).catch(() => { if (!cancelled) setCanReject(false); });
        return () => { cancelled = true; };
    }, [adminToken]);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedUploadSearchValue, setDebouncedUploadSearchValue] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [activeUploadEpisode, setActiveUploadEpisode] = useState(null);
    const [hoveredPreviewKey, setHoveredPreviewKey] = useState('');
    const [failedPreviewImageIds, setFailedPreviewImageIds] = useState({});
    const selectedOrderIdRef = useRef(selectedOrderId);

    useEffect(() => {
        selectedOrderIdRef.current = selectedOrderId;
        setSelectingBrowseTaskIds({});
    }, [selectedOrderId]);

    const selectedOrder = useMemo(
        () => eligibleOrders.find((order) => order.id === selectedOrderId) || null,
        [eligibleOrders, selectedOrderId],
    );
    const selectedOrderTaskIds = useMemo(() => (
        Array.from(new Set(
            (selectedOrder?.tasks || [])
                .map((task) => Number(task?.task_id))
                .filter((taskId) => Number.isInteger(taskId) && taskId > 0),
        )).sort((left, right) => left - right)
    ), [selectedOrder]);
    const selectedOrderTaskIdsKey = selectedOrderTaskIds.join(',');

    useEffect(() => {
        showNotificationRef.current = showNotification;
    }, [showNotification]);

    useEffect(() => {
        handleAdminSessionExpiredRef.current = handleAdminSessionExpired;
    }, [handleAdminSessionExpired]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedBrowseSearchValue(browseSearchValue.trim());
        }, 250);
        return () => window.clearTimeout(timeoutId);
    }, [browseSearchValue]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedUploadSearchValue(searchValue.trim());
        }, 250);
        return () => window.clearTimeout(timeoutId);
    }, [searchValue]);

    useEffect(() => {
        setUploadFilterOptions(null);
        if (!adminToken) return undefined;
        const controller = new AbortController();
        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-filter-options`, {
            headers: {Authorization: `Bearer ${adminToken}`},
            signal: controller.signal,
        })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json?.success) {
                    throw new Error(getApiErrorMessage(json, 'Failed to load upload filters.'));
                }
                setUploadFilterOptions(json.data || null);
            })
            .catch((loadError) => {
                if (loadError?.name === 'AbortError') return;
                console.error('Failed to load upload filter options', loadError);
                if (isSessionExpiredError(loadError)) handleAdminSessionExpiredRef.current?.();
            });
        return () => controller.abort();
    }, [adminToken]);

    useEffect(() => {
        setExpandedUploads({});
        setExpandedUploadVisibleCounts({});
    }, [debouncedUploadSearchValue, failedOnly, qaRejectedOnly, includeTestUploads, publicOnly, sortBy, uploadDateRange, uploadFilterSelections]);

    useEffect(() => {
        setExpandedBrowseTasks({});
        setExpandedBrowseTaskVisibleCounts({});
    }, [selectedOrderId, debouncedBrowseSearchValue]);

    useEffect(() => {
        if (!adminToken) {
            setEligibleOrders([]);
            setSelectedOrderId('');
            return;
        }
        let cancelled = false;
        setOrdersLoading(true);
        orderAdminApi.listOrders(adminToken)
            .then((orders) => {
                if (cancelled) return;
                const eligible = (Array.isArray(orders) ? orders : []).filter(
                    (order) => order.status === 'production' || order.status === 'delivering',
                );
                setEligibleOrders(eligible);
                setSelectedOrderId((current) => (
                    eligible.some((order) => order.id === current) ? current : ''
                ));
            })
            .catch((loadError) => {
                if (cancelled) return;
                console.error('Failed to load Orders for Episode assignment', loadError);
                setEligibleOrders([]);
                if (isSessionExpiredError(loadError)) handleAdminSessionExpiredRef.current?.();
            })
            .finally(() => {
                if (!cancelled) setOrdersLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [adminToken]);

    useEffect(() => {
        setSelectedOrderEpisodeIds([]);
        setSelectedOrderEpisodeDetails({});
        setAssignedOrderEpisodeIds([]);
        setAssignedOrderEpisodes([]);
        setAssignmentConfirmationOpen(false);
    }, [adminToken, selectedOrderId]);

    useEffect(() => {
        if (!adminToken || !selectedOrderId) return undefined;
        let cancelled = false;
        orderAdminApi.listOrderEpisodes(adminToken, selectedOrderId)
            .then((result) => {
                if (cancelled) return;
                const episodes = Array.isArray(result?.episodes) ? result.episodes : [];
                setAssignedOrderEpisodes(episodes);
                setAssignedOrderEpisodeIds(episodes.map((episode) => Number(episode.episode_id)));
            })
            .catch((loadError) => {
                if (cancelled) return;
                console.error('Failed to load existing Order Episodes', loadError);
                showNotificationRef.current?.('error', loadError.message || 'Failed to load existing Order Episodes.');
            });
        return () => {
            cancelled = true;
        };
    }, [adminToken, selectedOrderId, orderEpisodesRevision]);

    const loadData = useCallback(async ({append = false, offset = 0} = {}) => {
        const requestId = uploadGroupsRequestIdRef.current + 1;
        uploadGroupsRequestIdRef.current = requestId;
        uploadGroupsAbortControllerRef.current?.abort();
        uploadGroupsAbortControllerRef.current = null;
        let requestController = null;

        if (!adminToken) {
            setUploadGroups([]);
            setUploadGroupsOffset(0);
            setHasMoreUploadGroups(false);
            setEpisodePreviewUrls({});
            setEpisodePreviewImageUrls({});
            setEpisodePreviewLoadingIds({});
            setEpisodePreviewVideoStates({});
            setEpisodePreviewResolvedIds({});
            setPendingLoadMorePreviewIds([]);
            setError('');
            setIsLoading(false);
            setIsLoadingMoreUploadGroups(false);
            return;
        }

        if (adminView === 'browse' && (!selectedOrderId || !selectedOrderTaskIds.length)) {
            setUploadGroups([]);
            setUploadGroupsOffset(0);
            setHasMoreUploadGroups(false);
            setEpisodePreviewUrls({});
            setEpisodePreviewImageUrls({});
            setEpisodePreviewLoadingIds({});
            setEpisodePreviewVideoStates({});
            setEpisodePreviewResolvedIds({});
            setPendingLoadMorePreviewIds([]);
            setError('');
            setIsLoading(false);
            setIsLoadingMoreUploadGroups(false);
            return;
        }

        if (!hasAdminAccess) {
            setUploadGroups([]);
            setUploadGroupsOffset(0);
            setHasMoreUploadGroups(false);
            setEpisodePreviewUrls({});
            setEpisodePreviewImageUrls({});
            setEpisodePreviewLoadingIds({});
            setEpisodePreviewVideoStates({});
            setEpisodePreviewResolvedIds({});
            setPendingLoadMorePreviewIds([]);
            setError('Admin access required.');
            setIsLoading(false);
            setIsLoadingMoreUploadGroups(false);
            return;
        }

        if (append) {
            setIsLoadingMoreUploadGroups(true);
        } else {
            setIsLoading(true);
            setError('');
        }

        requestController = new AbortController();
        uploadGroupsAbortControllerRef.current = requestController;

        try {
            const isBrowseRequest = adminView === 'browse';
            const params = isBrowseRequest
                ? new URLSearchParams({
                    admin: 'true',
                    task_ids: selectedOrderTaskIdsKey,
                    episode_limit: String(MAX_EXPANDED_EPISODE_COUNT),
                })
                : new URLSearchParams({
                    admin: 'true',
                    limit: String(ADMIN_UPLOAD_GROUP_PAGE_SIZE),
                    offset: String(offset),
                    include_test_uploads: includeTestUploads ? 'true' : 'false',
                    failed_only: failedOnly ? 'true' : 'false',
                    public_only: publicOnly ? 'true' : 'false',
                    qa_rejected_only: qaRejectedOnly ? 'true' : 'false',
                });
            if (!isBrowseRequest) {
                [
                    ['product_names', uploadFilterSelections.robotArms],
                    ['operator_ids', uploadFilterSelections.operators],
                    ['task_ids', uploadFilterSelections.tasks],
                    ['job_ids', uploadFilterSelections.jobs],
                ].forEach(([name, values]) => {
                    if (values.length) params.set(name, values.join(','));
                });
                if (uploadDateRange) {
                    params.set('created_from', uploadDateRange.createdFrom);
                    params.set('created_to', uploadDateRange.createdTo);
                }
                if (debouncedUploadSearchValue) params.set('search', debouncedUploadSearchValue);
                params.set('sort', sortBy);
            }
            if (isBrowseRequest && debouncedBrowseSearchValue) {
                params.set('search', debouncedBrowseSearchValue);
            }
            if (isBrowseRequest && selectedOrderTaskIdsKey) {
                params.set('task_ids', selectedOrderTaskIdsKey);
            }
            const groupsResponse = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/admin/${isBrowseRequest ? 'downloadable-task-groups' : 'downloadable-upload-groups'}?${params.toString()}`,
                {
                    headers: {Authorization: `Bearer ${adminToken}`},
                    signal: requestController.signal,
                },
            );
            if (requestId !== uploadGroupsRequestIdRef.current) return;
            if (!groupsResponse.ok) {
                const err = await groupsResponse.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(err, 'Failed to load upload groups.'));
            }

            const groupsJson = await groupsResponse.json();
            if (requestId !== uploadGroupsRequestIdRef.current) return;
            const nextGroups = Array.isArray(groupsJson?.data) ? groupsJson.data : [];
            const pagination = isBrowseRequest ? {} : (groupsJson?.pagination || {});
            const nextOffset = Number.isFinite(Number(pagination?.next_offset))
                ? Number(pagination.next_offset)
                : offset + nextGroups.length;
            const nextPreviewIds = nextGroups.flatMap((group) => (
                Array.isArray(group?.episodes)
                    ? group.episodes.slice(0, COLLAPSED_EPISODE_COUNT).map((episode) => String(episode.episode_id))
                    : []
            )).filter(Boolean);

            setUploadGroups((current) => {
                if (!append || isBrowseRequest) return nextGroups;
                const merged = [...current];
                const existingIds = new Set(current.map((group) => String(group.upload_id)));
                nextGroups.forEach((group) => {
                    const key = String(group.upload_id);
                    if (existingIds.has(key)) return;
                    existingIds.add(key);
                    merged.push(group);
                });
                return merged;
            });
            setUploadGroupsOffset(nextOffset);
            setHasMoreUploadGroups(!isBrowseRequest && Boolean(pagination?.has_more));
            if (!append) {
                setEpisodePreviewUrls({});
                setEpisodePreviewImageUrls({});
                setEpisodePreviewLoadingIds({});
                setEpisodePreviewVideoStates({});
                setEpisodePreviewResolvedIds({});
                setPendingLoadMorePreviewIds([]);
            } else if (nextPreviewIds.length) {
                setEpisodePreviewLoadingIds((current) => {
                    const next = {...current};
                    nextPreviewIds.forEach((episodeId) => {
                        next[episodeId] = true;
                    });
                    return next;
                });
                setPendingLoadMorePreviewIds(nextPreviewIds);
            }
        } catch (fetchError) {
            if (fetchError?.name === 'AbortError' || requestId !== uploadGroupsRequestIdRef.current) return;
            const message = fetchError?.message || 'Failed to load admin data download.';
            console.error('RoboticDataAdmin load error:', fetchError);
            if (!append) {
                setError(message);
                setUploadGroups([]);
                setUploadGroupsOffset(0);
                setHasMoreUploadGroups(false);
                setEpisodePreviewUrls({});
                setEpisodePreviewImageUrls({});
                setEpisodePreviewLoadingIds({});
                setEpisodePreviewVideoStates({});
                setEpisodePreviewResolvedIds({});
                setPendingLoadMorePreviewIds([]);
            }
            if (isSessionExpiredError(message)) {
                handleAdminSessionExpiredRef.current?.();
                return;
            }
            if (typeof showNotificationRef.current === 'function' && message !== 'Admin access required.') {
                showNotificationRef.current('error', message);
            }
        } finally {
            if (requestId === uploadGroupsRequestIdRef.current) {
                if (uploadGroupsAbortControllerRef.current === requestController) {
                    uploadGroupsAbortControllerRef.current = null;
                }
                setIsLoading(false);
                setIsLoadingMoreUploadGroups(false);
            }
        }
    }, [adminToken, adminView, debouncedBrowseSearchValue, debouncedUploadSearchValue, failedOnly, qaRejectedOnly, hasAdminAccess, includeTestUploads, publicOnly, selectedOrderId, selectedOrderTaskIds.length, selectedOrderTaskIdsKey, sortBy, uploadDateRange, uploadFilterSelections]);

    useEffect(() => {
        void loadData({append: false, offset: 0});
    }, [loadData]);

    useEffect(() => () => {
        uploadGroupsRequestIdRef.current += 1;
        uploadGroupsAbortControllerRef.current?.abort();
        uploadGroupsAbortControllerRef.current = null;
    }, []);

    useEffect(() => {
        if (!failedOnly && !qaRejectedOnly) return;
        setEpisodePreviewUrls({});
        setEpisodePreviewImageUrls({});
        setEpisodePreviewLoadingIds({});
        setEpisodePreviewVideoStates({});
        setEpisodePreviewResolvedIds({});
        setPendingLoadMorePreviewIds([]);
    }, [failedOnly, qaRejectedOnly]);

    const visibleUploadGroups = useMemo(() => {
        const nextGroups = uploadGroups.filter((group) => (
            (qaRejectedOnly || shouldShowAdminUploadGroup(group.status))
            && Array.isArray(group.episodes)
            && group.episodes.length > 0
        ));

        // Uploads arrive already sorted by the server (the Sort dropdown is a request parameter).
        return nextGroups;
    }, [uploadGroups, qaRejectedOnly]);

    const browseAllScenarioGroups = useMemo(() => {
        const groups = new Map();
        visibleUploadGroups.forEach((upload) => {
            const taskId = Number(upload.task_id);
            const scenario = String(upload.scenario || `Task ${taskId || 'unknown'}`);
            const environment = String(upload.environment || 'Uncategorized');
            const key = String(taskId || scenario);
            const group = groups.get(key) || {
                key,
                taskId,
                scenario,
                environment,
                episodeCount: Number(upload.episode_count || 0),
                episodes: [],
            };
            group.episodeCount = Math.max(group.episodeCount, Number(upload.episode_count || 0));
            (upload.episodes || []).forEach((episode) => {
                group.episodes.push({
                    ...episode,
                    upload_id: episode.upload_id || upload.upload_id,
                    task_id: taskId,
                    scenario,
                    environment,
                    product_name: episode.product_name || upload.product_name,
                });
            });
            if (group.episodes.length) groups.set(key, group);
        });
        return Array.from(groups.values())
            .map((group) => ({
                ...group,
                episodes: sortEpisodesNewestFirst(group.episodes),
            }))
            .sort((left, right) => (
                left.environment.localeCompare(right.environment)
                || left.scenario.localeCompare(right.scenario)
            ));
    }, [visibleUploadGroups]);

    const browseScenarioGroups = useMemo(() => {
        const query = browseSearchValue.trim().toLowerCase();
        if (!query) return browseAllScenarioGroups;
        return browseAllScenarioGroups
            .map((group) => ({
                ...group,
                episodes: group.episodes.filter((episode) => (
                    matchesBrowseEpisodeSearch(group, episode, query)
                )),
            }))
            .filter((group) => group.episodes.length > 0);
    }, [browseAllScenarioGroups, browseSearchValue]);

    const visiblePreviewEpisodeIds = useMemo(() => {
        if (adminView === 'browse') {
            return browseScenarioGroups.flatMap((group) => {
                const isExpanded = Boolean(expandedBrowseTasks[group.taskId]);
                const visibleCount = isExpanded
                    ? Math.min(
                        Number(expandedBrowseTaskVisibleCounts[group.taskId] || EXPANDED_PAGE_SIZE),
                        MAX_EXPANDED_EPISODE_COUNT,
                    )
                    : COLLAPSED_EPISODE_COUNT;
                return group.episodes
                    .slice(0, visibleCount)
                    .map((episode) => String(episode.episode_id));
            });
        }
        const ids = [];
        visibleUploadGroups.forEach((group) => {
            const uploadId = String(group.upload_id);
            const isExpanded = Boolean(expandedUploads[uploadId]);
            const visibleCount = isExpanded
                ? Number(expandedUploadVisibleCounts[uploadId] || EXPANDED_PAGE_SIZE)
                : COLLAPSED_EPISODE_COUNT;
            (group.episodes || []).slice(0, visibleCount).forEach((episode) => {
                ids.push(String(episode.episode_id));
            });
        });
        return ids;
    }, [adminView, browseScenarioGroups, expandedBrowseTaskVisibleCounts, expandedBrowseTasks, expandedUploadVisibleCounts, expandedUploads, visibleUploadGroups]);

    useEffect(() => {
        if ((adminView === 'uploads' && (failedOnly || qaRejectedOnly)) || !adminToken || !hasAdminAccess || !visiblePreviewEpisodeIds.length) return undefined;

        const missingEpisodeIds = visiblePreviewEpisodeIds.filter(
            (episodeId) => !episodePreviewResolvedIds[String(episodeId)],
        );
        if (!missingEpisodeIds.length) return undefined;

        let isCancelled = false;

        setEpisodePreviewLoadingIds((current) => {
            const next = {...current};
            missingEpisodeIds.forEach((episodeId) => {
                next[String(episodeId)] = true;
            });
            return next;
        });

        const loadPreviews = async () => {
            try {
                const chunkSize = 60;
                for (let start = 0; start < missingEpisodeIds.length; start += chunkSize) {
                    const batchEpisodeIds = missingEpisodeIds.slice(start, start + chunkSize);
                    // Uploads reads the Derived bucket; Assign Episodes to Orders keeps the Preview bucket.
                    const response = await fetch(
                        adminView === 'uploads'
                            ? `${PRISMAX_DATA_PIPELINE_URL}/data/admin/episodes/derived-previews`
                            : `${PRISMAX_DATA_PIPELINE_URL}/data/downloadable-uploads/previews`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${adminToken}`,
                            },
                            body: JSON.stringify(adminView === 'uploads'
                                ? {episode_ids: batchEpisodeIds.map(Number)}
                                : {admin: true, selected_episode_ids: batchEpisodeIds}),
                        },
                    );

                    if (!response.ok) {
                        const err = await response.json().catch(() => ({}));
                        throw new Error(getApiErrorMessage(err, 'Failed to load preview videos.'));
                    }

                    const json = await response.json().catch(() => ({}));
                    const rows = Array.isArray(json?.data) ? json.data : [];
                    if (isCancelled) return;

                    setEpisodePreviewUrls((current) => {
                        const next = {...current};
                        rows.forEach((row) => {
                            const episodeId = String(row?.episode_id || '').trim();
                            if (!episodeId) return;
                            next[episodeId] = String(row?.preview_video_url || '').trim();
                        });
                        return next;
                    });
                    setEpisodePreviewImageUrls((current) => {
                        const next = {...current};
                        rows.forEach((row) => {
                            const episodeId = String(row?.episode_id || '').trim();
                            if (!episodeId) return;
                            next[episodeId] = String(row?.preview_image_url || '').trim();
                        });
                        return next;
                    });
                    setEpisodePreviewVideoStates((current) => {
                        const next = {...current};
                        rows.forEach((row) => {
                            const episodeId = String(row?.episode_id || '').trim();
                            if (!episodeId) return;
                            next[episodeId] = row?.preview_video_url ? 'loading' : 'error';
                        });
                        return next;
                    });
                    setEpisodePreviewLoadingIds((current) => {
                        const next = {...current};
                        batchEpisodeIds.forEach((episodeId) => {
                            delete next[String(episodeId)];
                        });
                        return next;
                    });
                }
            } catch (previewError) {
                if (!isCancelled) {
                    console.error('RoboticDataAdmin preview load error:', previewError);
                    if (isSessionExpiredError(previewError)) {
                        handleAdminSessionExpiredRef.current?.();
                    }
                }
            } finally {
                if (isCancelled) return;
                setEpisodePreviewResolvedIds((current) => {
                    const next = {...current};
                    missingEpisodeIds.forEach((episodeId) => {
                        next[String(episodeId)] = true;
                    });
                    return next;
                });
                setEpisodePreviewLoadingIds((current) => {
                    const next = {...current};
                    missingEpisodeIds.forEach((episodeId) => {
                        delete next[String(episodeId)];
                    });
                    return next;
                });
            }
        };

        void loadPreviews();

        return () => {
            isCancelled = true;
        };
    }, [adminToken, adminView, episodePreviewResolvedIds, failedOnly, qaRejectedOnly, hasAdminAccess, visiblePreviewEpisodeIds]);

    useEffect(() => {
        if (!pendingLoadMorePreviewIds.length) return;
        const stillLoading = pendingLoadMorePreviewIds.some((episodeId) => Boolean(episodePreviewLoadingIds[String(episodeId)]));
        if (!stillLoading) {
            setPendingLoadMorePreviewIds([]);
        }
    }, [episodePreviewLoadingIds, pendingLoadMorePreviewIds]);

    const handleShareEpisode = async (episodeId) => {
        const shareBaseUrl = window.location.hostname === 'app.prismax.ai'
            ? 'https://www.prismax.ai'
            : 'https://beta-www.prismax.ai';
        const shareUrl = `${shareBaseUrl}/robotic-data/${episodeId}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            showNotification?.('success', 'Episode link copied.');
        } catch (error) {
            showNotification?.('warning', 'Failed to copy episode link.');
        }
    };

    const handleToggleUploadExpand = (uploadId) => {
        setExpandedUploads((current) => ({
            ...current,
            [uploadId]: !current[uploadId],
        }));
        setExpandedUploadVisibleCounts((current) => {
            if (current[uploadId]) {
                const next = {...current};
                delete next[uploadId];
                return next;
            }
            return {
                ...current,
                [uploadId]: EXPANDED_PAGE_SIZE,
            };
        });
    };

    const handleLoadMoreUploadEpisodes = (uploadId) => {
        setExpandedUploadVisibleCounts((current) => ({
            ...current,
            [uploadId]: Number(current[uploadId] || EXPANDED_PAGE_SIZE) + EXPANDED_PAGE_SIZE,
        }));
    };

    const handleLoadMoreUploadGroups = () => {
        if (isLoading || isLoadingMoreUploadGroups || !hasMoreUploadGroups) return;
        void loadData({append: true, offset: uploadGroupsOffset});
    };

    const handleToggleBrowseExpand = (taskId) => {
        setExpandedBrowseTasks((current) => ({
            ...current,
            [taskId]: !current[taskId],
        }));
        setExpandedBrowseTaskVisibleCounts((current) => {
            if (current[taskId]) {
                const next = {...current};
                delete next[taskId];
                return next;
            }
            return {
                ...current,
                [taskId]: EXPANDED_PAGE_SIZE,
            };
        });
    };

    const handleLoadMoreBrowseEpisodes = (taskId) => {
        setExpandedBrowseTaskVisibleCounts((current) => ({
            ...current,
            [taskId]: Math.min(
                Number(current[taskId] || EXPANDED_PAGE_SIZE) + EXPANDED_PAGE_SIZE,
                MAX_EXPANDED_EPISODE_COUNT,
            ),
        }));
    };

    const handleToggleOrderEpisode = (episode) => {
        const normalizedId = Number(episode?.episode_id ?? episode);
        if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;
        if (assignedOrderEpisodeIds.includes(normalizedId)) return;
        const shouldSelect = !selectedOrderEpisodeIds.includes(normalizedId);
        setSelectedOrderEpisodeIds((current) => (
            shouldSelect ? [...current, normalizedId] : current.filter((value) => value !== normalizedId)
        ));
        setSelectedOrderEpisodeDetails((current) => {
            const next = {...current};
            if (shouldSelect && episode && typeof episode === 'object') next[normalizedId] = episode;
            else delete next[normalizedId];
            return next;
        });
    };

    const handleToggleOrderEpisodes = (episodes, shouldSelect) => {
        const normalizedEpisodes = (Array.isArray(episodes) ? episodes : [])
            .filter((episode) => episode && typeof episode === 'object')
            .map((episode) => ({...episode, episode_id: Number(episode.episode_id)}))
            .filter((episode) => (
                Number.isInteger(episode.episode_id)
                && episode.episode_id > 0
                && !assignedOrderEpisodeIds.includes(episode.episode_id)
            ));
        const episodeIds = new Set(normalizedEpisodes.map((episode) => episode.episode_id));
        setSelectedOrderEpisodeIds((current) => {
            if (!shouldSelect) return current.filter((episodeId) => !episodeIds.has(episodeId));
            return Array.from(new Set([...current, ...episodeIds]));
        });
        setSelectedOrderEpisodeDetails((current) => {
            const next = {...current};
            normalizedEpisodes.forEach((episode) => {
                if (shouldSelect) next[episode.episode_id] = episode;
                else delete next[episode.episode_id];
            });
            return next;
        });
    };

    const handleToggleAllOrderTaskEpisodes = async (group, shouldSelect) => {
        const taskId = Number(group?.taskId);
        if (!Number.isInteger(taskId) || taskId <= 0) return;

        if (!shouldSelect) {
            const taskEpisodes = Object.values(selectedOrderEpisodeDetails).filter(
                (episode) => Number(episode?.task_id) === taskId
                    && matchesBrowseEpisodeSearch(group, episode, browseSearchValue),
            );
            handleToggleOrderEpisodes(taskEpisodes, false);
            return;
        }
        if (!adminToken || selectingBrowseTaskIds[taskId]) return;

        const requestOrderId = selectedOrderId;
        setSelectingBrowseTaskIds((current) => ({...current, [taskId]: true}));
        try {
            const allTaskEpisodes = await orderAdminApi.listAllDownloadableTaskEpisodes(
                adminToken,
                taskId,
                {jobId: getBrowseJobId(browseSearchValue)},
            );
            if (selectedOrderIdRef.current !== requestOrderId) return;
            const matchingEpisodes = allTaskEpisodes.filter((episode) => (
                matchesBrowseEpisodeSearch(group, episode, browseSearchValue)
            ));
            handleToggleOrderEpisodes(matchingEpisodes, true);
        } catch (loadError) {
            console.error('Failed to select all Task Episodes', loadError);
            if (isSessionExpiredError(loadError)) handleAdminSessionExpiredRef.current?.();
            showNotificationRef.current?.('error', loadError.message || 'Failed to select all Episodes.');
        } finally {
            if (selectedOrderIdRef.current === requestOrderId) {
                setSelectingBrowseTaskIds((current) => {
                    const next = {...current};
                    delete next[taskId];
                    return next;
                });
            }
        }
    };

    const handleClearOrderEpisodeSelection = () => {
        setSelectedOrderEpisodeIds([]);
        setSelectedOrderEpisodeDetails({});
    };

    const handleAssignOrderEpisodes = async () => {
        if (!selectedOrderId || !selectedOrderEpisodeIds.length || isAssigningEpisodes) return;
        setIsAssigningEpisodes(true);
        try {
            const result = await orderAdminApi.addOrderEpisodes(
                adminToken,
                selectedOrderId,
                selectedOrderEpisodeIds,
            );
            const addedIds = (result?.added_episode_ids || []).map(Number);
            const existingIds = (result?.already_in_order_episode_ids || []).map(Number);
            if (result?.order?.id) {
                setEligibleOrders((current) => current.map((order) => (
                    order.id === result.order.id ? result.order : order
                )));
            }
            setAssignedOrderEpisodeIds((current) => Array.from(new Set([
                ...current,
                ...addedIds,
                ...existingIds,
            ])));
            const assignedIds = new Set([...addedIds, ...existingIds]);
            setAssignedOrderEpisodes((current) => {
                const episodesById = new Map(
                    current.map((episode) => [Number(episode.episode_id), episode]),
                );
                selectedOrderEpisodeIds.forEach((episodeId) => {
                    if (!assignedIds.has(episodeId)) return;
                    const episode = selectedOrderEpisodeDetails[episodeId];
                    if (episode) episodesById.set(episodeId, episode);
                });
                return Array.from(episodesById.values());
            });
            setSelectedOrderEpisodeIds([]);
            setSelectedOrderEpisodeDetails({});
            setAssignmentConfirmationOpen(false);
            showNotificationRef.current?.(
                'success',
                addedIds.length
                    ? `${addedIds.length} episode${addedIds.length === 1 ? '' : 's'} added to ${selectedOrderId}.`
                    : `All selected episodes were already in ${selectedOrderId}.`,
            );
        } catch (assignError) {
            console.error('Failed to add Episodes to Order', assignError);
            if (isSessionExpiredError(assignError)) handleAdminSessionExpiredRef.current?.();
            showNotificationRef.current?.('error', assignError.message || 'Failed to add Episodes to Order.');
        } finally {
            setIsAssigningEpisodes(false);
        }
    };

    const isLoadMoreBusy = isLoadingMoreUploadGroups || pendingLoadMorePreviewIds.length > 0;

    return (
        <div className={styles.workspace}>
            {(rejectingEpisode || rejectingUpload) && <EpisodeRejectionModal key={rejectingUpload ? `upload-${rejectingUpload.upload_id}` : `episode-${rejectingEpisode.episode_id}`} episode={rejectingEpisode} upload={rejectingUpload} adminToken={adminToken} onClose={() => { setRejectingEpisode(null); setRejectingUpload(null); }} onRejected={(data) => {
                const ids = new Set((data.rejected_episode_ids
                    ? [...data.rejected_episode_ids, ...(data.already_rejected_episode_ids || [])]
                    : [data.episode_id]).map(Number));
                setRejectingEpisode(null);
                setRejectingUpload(null);
                setActiveUploadEpisode(null);
                setDownloadEpisodeDetails((current) => Object.fromEntries(Object.entries(current).filter(([id]) => !ids.has(Number(id)))));
                setSelectedOrderEpisodeIds((current) => current.filter((id) => !ids.has(Number(id))));
                setSelectedOrderEpisodeDetails((current) => Object.fromEntries(Object.entries(current).filter(([id]) => !ids.has(Number(id)))));
                setAssignedOrderEpisodeIds((current) => current.filter((id) => !ids.has(Number(id))));
                setAssignedOrderEpisodes((current) => current.filter((episode) => !ids.has(Number(episode.episode_id))));
                if (data.rejected_count !== undefined) showNotificationRef.current?.('success', `${data.rejected_count} episodes rejected; ${data.already_rejected_count} already rejected; ${data.skipped_count} processing or failed episodes skipped.`);
                setOrderEpisodesRevision((revision) => revision + 1);
                setEpisodePreviewUrls({});
                setEpisodePreviewImageUrls({});
                setEpisodePreviewResolvedIds({});
                void loadData({append: false, offset: 0});
            }}/>}

            <section className={styles.centerPanel}>
                <div className={styles.adminViewTabs}>
                    <button
                        type="button"
                        className={`${styles.adminViewTab} ${styles.adminViewTabButton} ${adminView === 'browse' ? styles.adminViewTabActive : ''}`}
                        onClick={() => setAdminView('browse')}
                        disabled={isAdminDownloading}
                    >
                        <ScenariosIcon/>
                        <span>Assign Episodes to Orders</span>
                        <span className={styles.adminBadge}>Admin</span>
                    </button>
                    <button
                        type="button"
                        className={`${styles.adminViewTab} ${styles.adminViewTabButton} ${adminView === 'uploads' ? styles.adminViewTabActive : ''}`}
                        onClick={() => setAdminView('uploads')}
                    >
                        <UploadTabIcon/>
                        <span>Uploads</span>
                        <span className={styles.adminBadge}>Admin</span>
                    </button>
                </div>

                {adminView === 'browse' ? (
                    <OrderEpisodeBrowser
                        adminToken={adminToken}
                        orders={eligibleOrders}
                        selectedOrderId={selectedOrderId}
                        onSelectOrder={setSelectedOrderId}
                        scenarioGroups={browseScenarioGroups}
                        selectedEpisodeIds={selectedOrderEpisodeIds}
                        selectedEpisodes={selectedOrderEpisodeIds
                            .map((episodeId) => selectedOrderEpisodeDetails[episodeId])
                            .filter(Boolean)}
                        assignedEpisodes={assignedOrderEpisodes}
                        assignedEpisodeIds={assignedOrderEpisodeIds}
                        onToggleEpisode={handleToggleOrderEpisode}
                        onToggleEpisodes={handleToggleOrderEpisodes}
                        onToggleTaskEpisodes={handleToggleAllOrderTaskEpisodes}
                        onClearSelection={handleClearOrderEpisodeSelection}
                        previewVideoUrls={episodePreviewUrls}
                        previewImageUrls={episodePreviewImageUrls}
                        previewLoadingIds={episodePreviewLoadingIds}
                        searchValue={browseSearchValue}
                        onSearch={setBrowseSearchValue}
                        loading={isLoading || ordersLoading}
                        error={error}
                        expandedTaskIds={expandedBrowseTasks}
                        expandedTaskVisibleCounts={expandedBrowseTaskVisibleCounts}
                        onToggleExpand={handleToggleBrowseExpand}
                        onLoadMoreEpisodes={handleLoadMoreBrowseEpisodes}
                        onRequestAssign={() => setAssignmentConfirmationOpen(true)}
                        selectingTaskIds={selectingBrowseTaskIds}
                        onRejectEpisode={canReject ? setRejectingEpisode : undefined}
                    />
                ) : (
                    <>
                <AdminUploadFilters
                    options={uploadFilterOptions}
                    includeTestUploads={includeTestUploads}
                    onIncludeTestUploadsChange={(next) => {
                        setIncludeTestUploads(next);
                        const testTaskId = uploadFilterOptions?.test_upload_task_id;
                        if (!next && testTaskId) {
                            setUploadFilterSelections((current) => (
                                current.tasks.some((taskId) => Number(taskId) === Number(testTaskId))
                                    ? {...current, tasks: current.tasks.filter((taskId) => Number(taskId) !== Number(testTaskId))}
                                    : current
                            ));
                        }
                    }}
                    failedOnly={failedOnly}
                    onFailedOnlyChange={(next) => {
                        setFailedOnly(next);
                        if (next) {
                            setPublicOnly(false);
                            setQaRejectedOnly(false);
                        }
                    }}
                    publicOnly={publicOnly}
                    onPublicOnlyChange={(next) => {
                        setPublicOnly(next);
                        if (next) {
                            setFailedOnly(false);
                            setQaRejectedOnly(false);
                        }
                    }}
                    qaRejectedOnly={qaRejectedOnly}
                    onQaRejectedOnlyChange={(next) => {
                        setQaRejectedOnly(next);
                        if (next) {
                            setFailedOnly(false);
                            setPublicOnly(false);
                        }
                    }}
                    selections={uploadFilterSelections}
                    onSelectionChange={(key, values) => setUploadFilterSelections((current) => ({...current, [key]: values}))}
                    dateRange={uploadDateRange}
                    onDateRangeChange={setUploadDateRange}
                    onReset={() => {
                        setUploadFilterSelections(EMPTY_UPLOAD_FILTER_SELECTIONS);
                        setUploadDateRange(null);
                    }}
                />

                <div className={styles.adminUploadsLayout}>
                <div className={styles.adminContentArea}>
                    <div className={styles.uploadsToolbar}>
                        <div className={styles.uploadSearchWrap}>
                            <span className={styles.uploadSearchIcon}><SearchIcon/></span>
                            <input
                                className={styles.uploadSearch}
                                type="text"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                placeholder="Search by upload ID or episode ID…"
                                aria-label="Search by upload ID or episode ID"
                            />
                        </div>
                        <div className={styles.uploadSortWrap}>
                            <span className={styles.uploadSortLabel}>Sort by</span>
                            <select
                                className={styles.uploadSortSelect}
                                value={sortBy}
                                onChange={(event) => setSortBy(event.target.value)}
                            >
                                <option value="recent">Most recent</option>
                                <option value="oldest">Oldest</option>
                                <option value="size">File size</option>
                                <option value="episodes">Episode count</option>
                            </select>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className={styles.emptyState}>Loading uploads...</div>
                    ) : null}

                    {!isLoading && error ? (
                        <div className={styles.emptyState}>{error}</div>
                    ) : null}

                    {!isLoading && !error && !visibleUploadGroups.length ? (
                        <div className={styles.emptyState}>No uploads match the current search.</div>
                    ) : null}

                    {!isLoading && !error && visibleUploadGroups.map((group, groupIndex) => {
                        const uploadId = String(group.upload_id);
                        const isFailedView = Boolean(failedOnly || qaRejectedOnly);
                        const isExpanded = Boolean(expandedUploads[uploadId]);
                        const visibleCount = isExpanded
                            ? Number(expandedUploadVisibleCounts[uploadId] || EXPANDED_PAGE_SIZE)
                            : COLLAPSED_EPISODE_COUNT;
                        const visibleEpisodes = (group.episodes || []).slice(0, visibleCount);
                        const hasMoreEpisodes = isExpanded && visibleCount < (group.episodes || []).length;

                        return (
                            <article key={uploadId} className={styles.taskSection}>
                                <div className={styles.uploadHeader}>
                                    <span className={styles.uploadBadge}>{group.upload_label || `UPL-${uploadId}`}</span>
                                    {shouldShowUploadStatus(group.display_status || group.status) ? (
                                        <span
                                            className={`${styles.uploadStatusBadge} ${isFailedUploadStatus(group.display_status || group.status)
                                                ? styles.uploadStatusBadgeFailed
                                                : isPartialUploadStatus(group.display_status || group.status)
                                                    ? styles.uploadStatusBadgePartial
                                                    : styles.uploadStatusBadgeHealthy}`}
                                        >
                                            {String(group.display_status || group.status || 'UNKNOWN')}
                                        </span>
                                    ) : null}
                                    <button
                                        type="button"
                                        className={styles.taskChevron}
                                        onClick={() => handleToggleUploadExpand(uploadId)}
                                        aria-label={isExpanded ? 'Collapse upload' : 'Expand upload'}
                                    >
                                        <img
                                            src={arrowUpIcon}
                                            alt=""
                                            className={isExpanded ? styles.taskChevronIconOpen : styles.taskChevronIcon}
                                        />
                                    </button>
                                    <span className={styles.uploadMeta}>
                                        {(group.episode_count || 0)} episodes · {group.scenario || `Upload ${uploadId}`}
                                    </span>
                                    {canReject && <button
                                        type="button"
                                        className={`${styles.filterChip} ${styles.filterDangerChip}`}
                                        aria-label={`Reject all episodes in upload ${uploadId}`}
                                        disabled={isAdminDownloading}
                                        onClick={() => { setRejectingEpisode(null); setActiveUploadEpisode(null); setRejectingUpload(group); }}
                                    >Reject all episodes</button>}
                                    <button
                                        type="button"
                                        className={`${styles.expandButton} ${isExpanded ? styles.expandButtonExpanded : ''}`}
                                        onClick={() => handleToggleUploadExpand(uploadId)}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                            {isExpanded ? <path d="M2 7h10"/> : (<><path d="M2 5h10"/><path d="M2 9h10"/></>)}
                                        </svg>
                                        <span>{isExpanded ? 'Collapse' : 'Expand data sample'}</span>
                                    </button>
                                </div>

                                <div className={styles.videoGridWrap}>
                                    <div className={styles.videoGrid}>
                                        {visibleEpisodes.map((episode, episodeIndex) => {
                                            const previewKey = String(episode.episode_id);
                                            const rejectionRows = isFailedView ? getEpisodeRejectionRows(episode) : [];
                                            const previewVideoUrl = String(episodePreviewUrls[previewKey] || '').trim();
                                            const previewImageUrl = String(episodePreviewImageUrls[previewKey] || '').trim();
                                            const previewVideoState = String(episodePreviewVideoStates[previewKey] || '').trim().toLowerCase();
                                            // No derived video means the colored card, even if a still image exists.
                                            const hasDerivedVideo = Boolean(previewVideoUrl) && previewVideoState !== 'error';
                                            const showPreviewImage = hasDerivedVideo && Boolean(previewImageUrl) && !failedPreviewImageIds[previewKey];
                                            const showPreviewVideo = hasDerivedVideo && hoveredPreviewKey === previewKey;
                                            const showPreviewSkeleton = !isFailedView && (
                                                Boolean(episodePreviewLoadingIds[previewKey])
                                                || (visiblePreviewEpisodeIds.includes(previewKey) && !episodePreviewResolvedIds[previewKey])
                                            );
                                            const openEpisode = () => setActiveUploadEpisode({
                                                episode,
                                                group,
                                                previewImageUrl: showPreviewImage ? previewImageUrl : '',
                                            });
                                            return (
                                                <div
                                                    key={episode.episode_id}
                                                    className={`${styles.videoCard} ${isFailedView ? styles.videoCardFailed : ''}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Open episode ${episode.episode_id} details`}
                                                    onMouseEnter={() => setHoveredPreviewKey(previewKey)}
                                                    onMouseLeave={() => setHoveredPreviewKey((current) => (current === previewKey ? '' : current))}
                                                    onClick={openEpisode}
                                                    onKeyDown={(event) => {
                                                        if (event.target !== event.currentTarget) return;
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            openEpisode();
                                                        }
                                                    }}
                                                >
                                                    <div
                                                        className={styles.videoThumb}
                                                        style={showPreviewImage || showPreviewVideo ? undefined : {background: getEpisodeColor(groupIndex, episodeIndex)}}
                                                    >
                                                        {showPreviewImage ? (
                                                            <img
                                                                className={styles.previewVideo}
                                                                src={previewImageUrl}
                                                                alt=""
                                                                loading="lazy"
                                                                onError={() => setFailedPreviewImageIds((current) => ({...current, [previewKey]: true}))}
                                                            />
                                                        ) : null}
                                                        {showPreviewVideo ? (
                                                            <video
                                                                className={styles.previewVideo}
                                                                src={previewVideoUrl}
                                                                poster={showPreviewImage ? previewImageUrl : undefined}
                                                                muted
                                                                autoPlay
                                                                loop
                                                                playsInline
                                                                preload="auto"
                                                                onError={() => {
                                                                    setEpisodePreviewVideoStates((current) => ({
                                                                        ...current,
                                                                        [previewKey]: 'error',
                                                                    }));
                                                                }}
                                                            />
                                                        ) : null}
                                                        {!showPreviewImage && !showPreviewVideo ? <span className={styles.playButton}/> : null}
                                                        {showPreviewSkeleton ? <div className={styles.thumbSkeleton}/> : null}
                                                        <div className={styles.hoverOverlay}>
                                                            {episode.share_available ? (
                                                                <div className={styles.videoActions}>
                                                                    <button
                                                                        type="button"
                                                                        className={styles.videoActionButton}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            void handleShareEpisode(episode.episode_id);
                                                                        }}
                                                                        aria-label="Share episode"
                                                                    >
                                                                        <ShareIcon/>
                                                                    </button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className={styles.adminTileInfo}>
                                                        {!qaRejectedOnly && (
                                                            <label className={styles.downloadSelect} onClick={(event) => event.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    aria-label={`Select episode ${episode.episode_id} for download`}
                                                                    checked={downloadEpisodeIds.includes(Number(episode.episode_id))}
                                                                    disabled={isAdminDownloading || (!downloadEpisodeIds.includes(Number(episode.episode_id)) && downloadEpisodeIds.length >= ADMIN_DOWNLOAD_MAX_EPISODES)}
                                                                    onChange={() => toggleDownloadEpisode(episode)}
                                                                />
                                                                Select for download
                                                            </label>
                                                        )}
                                                        {canReject && episode.status === 'DERIVED_READY' && <button type="button" className={`${styles.filterChip} ${styles.filterDangerChip}`} onClick={(event) => { event.stopPropagation(); setActiveUploadEpisode(null); setRejectingEpisode(episode); }}>Reject episode</button>}
                                                        {episode.status === 'QA_REJECTED' && <button type="button" className={styles.filterChip} onClick={(event) => { event.stopPropagation(); openEpisode(); }}>Review video</button>}
                                                        <p className={styles.adminTileMachine}>{episode.product_name || group.product_name || `Machine ${group.machine_id || '-'}`}</p>
                                                        <div className={styles.adminTileMetaRow}>
                                                            <span>Ep {episode.episode_id}</span>
                                                            {isFailedView ? (
                                                                <span className={styles.failedEpisodeBadge}>{episode.status === 'QA_REJECTED' ? 'QA rejected' : 'Rejected'}</span>
                                                            ) : (
                                                                <span>Q: {episode.quality_score ?? '-'}</span>
                                                            )}
                                                        </div>
                                                        {isFailedView ? (
                                                            <div className={styles.rejectionBlock}>
                                                                <div className={styles.rejectionTitle}>
                                                                    Rejection reason{rejectionRows.length > 1 ? 's' : ''}
                                                                </div>
                                                                <div className={styles.rejectionList}>
                                                                    {rejectionRows.map((row) => (
                                                                        <div key={row.key} className={styles.rejectionRow}>
                                                                            <span className={styles.rejectionDesc}>
                                                                                {row.description}
                                                                                {row.detail ? <span className={styles.rejectionDetail}>{row.detail}</span> : null}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {hasMoreEpisodes ? (
                                        <button
                                            type="button"
                                            className={styles.loadMoreButton}
                                            onClick={() => handleLoadMoreUploadEpisodes(uploadId)}
                                        >
                                            More
                                        </button>
                                    ) : null}
                                </div>

                                <div className={styles.uploadFoot}>
                                    <span>{group.file_count || 0} files</span>
                                    <span className={styles.dot}/>
                                    <span>{formatBytes(group.total_bytes || 0)}</span>
                                    <span className={styles.dot}/>
                                    <span>{formatShortDate(group.created_at)}</span>
                                    <span className={styles.dot}/>
                                    <span>{Number(group.successful_episode_count || 0)} successful</span>
                                    {Number(group.qa_rejected_episode_count || 0) > 0 && <><span className={styles.dot}/><span>{group.qa_rejected_episode_count} QA rejected</span></>}
                                    {Number(group.failed_episode_count || 0) > 0 ? (
                                        <>
                                            <span className={styles.dot}/>
                                            <span>{Number(group.failed_episode_count || 0)} failed</span>
                                        </>
                                    ) : null}
                                    {group.showing_successful_only ? (
                                        <>
                                            <span className={styles.dot}/>
                                            <span className={styles.uploadPartialNote}>
                                                {qaRejectedOnly ? 'Showing QA rejected data only' : isFailedView ? 'Showing failed data only' : 'Showing successful data only'}
                                            </span>
                                        </>
                                    ) : null}
                                </div>
                            </article>
                        );
                    })}
                    {!isLoading && !error && hasMoreUploadGroups ? (
                        <div className={styles.groupLoadMoreWrap}>
                            <button
                                type="button"
                                className={styles.loadMoreButton}
                                onClick={handleLoadMoreUploadGroups}
                                disabled={isLoadMoreBusy}
                            >
                                {isLoadMoreBusy ? 'Loading...' : 'Load more'}
                            </button>
                        </div>
                    ) : null}
                </div>
                    {activeUploadEpisode ? (
                        <AdminUploadEpisodeModal
                            adminToken={adminToken}
                            episode={activeUploadEpisode.episode}
                            upload={activeUploadEpisode.group}
                            previewImageUrl={activeUploadEpisode.previewImageUrl}
                            isSelected={downloadEpisodeIds.includes(Number(activeUploadEpisode.episode.episode_id))}
                            selectDisabled={activeUploadEpisode.episode.status === 'QA_REJECTED' || isAdminDownloading || (
                                !downloadEpisodeIds.includes(Number(activeUploadEpisode.episode.episode_id))
                                && downloadEpisodeIds.length >= ADMIN_DOWNLOAD_MAX_EPISODES
                            )}
                            onToggleSelect={() => toggleDownloadEpisode(activeUploadEpisode.episode)}
                            onClose={() => setActiveUploadEpisode(null)}
                            onSessionExpired={handleAdminSessionExpired}
                        />
                    ) : null}
                    <AdminBrowserDownloads
                        key={adminToken}
                        adminToken={adminToken}
                        episodeIds={downloadEpisodeIds}
                        selectedEpisodes={downloadEpisodeIds.map((id) => downloadEpisodeDetails[id])}
                        onClear={() => setDownloadEpisodeDetails({})}
                        onRemove={removeDownloadEpisode}
                        onBusyChange={setIsAdminDownloading}
                        onSessionExpired={handleAdminSessionExpired}
                    />
                </div>
                    </>
                )}

                {assignmentConfirmationOpen ? (
                    <div className={styles.assignmentModalBackdrop} role="presentation">
                        <div className={styles.assignmentModal} role="dialog" aria-modal="true" aria-labelledby="assign-order-episodes-title">
                            <h2 id="assign-order-episodes-title">Add Episodes to {selectedOrderId}?</h2>
                            <p>
                                This gives the Order access to {selectedOrderEpisodeIds.length} selected Episode{selectedOrderEpisodeIds.length === 1 ? '' : 's'}.
                                Original Jobs, Uploads, and operator payments will not change.
                            </p>
                            <div className={styles.assignmentModalActions}>
                                <button type="button" onClick={() => setAssignmentConfirmationOpen(false)} disabled={isAssigningEpisodes}>Cancel</button>
                                <button type="button" className={styles.assignOrderButton} onClick={() => void handleAssignOrderEpisodes()} disabled={isAssigningEpisodes}>
                                    {isAssigningEpisodes ? 'Adding…' : `Add to ${selectedOrderId}`}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </section>

        </div>
    );
};

export default RoboticDataAdmin;
